import {
  projectFertilityPrediction,
  type ProjectionStageId,
} from "@src/packages/fertility-projections";

export interface FunnelStage {
  label: string;
  count: number;
  color: string;
  detail?: string;
}

type ObservedFunnelStage = FunnelStage & { id: ProjectionStageId };

/**
 * Build funnel stages from retrieval data.
 * Pass available data — missing stages are omitted.
 */
export function buildEggFreezingFunnel(data: {
  eggsCollected?: number | null;
  matureEggs?: number | null;
  frozenEggs?: number | null;
}): FunnelStage[] {
  const stages: ObservedFunnelStage[] = [];
  if (data.eggsCollected != null && data.eggsCollected > 0) {
    stages.push({
      id: "retrieved_oocytes",
      label: "Eggs Retrieved",
      count: data.eggsCollected,
      color: "#8B3A1A",
    });
  }
  if (data.matureEggs != null && data.matureEggs > 0) {
    stages.push({
      id: "mature_oocytes",
      label: "Mature (MII)",
      count: data.matureEggs,
      color: "#A8563A",
    });
  }
  if (data.frozenEggs != null && data.frozenEggs > 0) {
    stages.push({
      id: "vitrified_oocytes",
      label: "Frozen",
      count: data.frozenEggs,
      color: "#4E7342",
    });
  }
  return projectObservedFunnel(stages);
}

export function buildEmbryoFreezingFunnel(data: {
  eggsCollected?: number | null;
  matureEggs?: number | null;
  fertilized?: number | null;
  day3Embryos?: number | null;
  blastocysts?: number | null;
  frozenEmbryos?: number | null;
  euploid?: number | null;
  noResult?: number | null;
}): FunnelStage[] {
  const stages: ObservedFunnelStage[] = [];
  if (data.eggsCollected != null && data.eggsCollected >= 0) {
    stages.push({
      id: "retrieved_oocytes",
      label: "Eggs Retrieved",
      count: data.eggsCollected,
      color: "#8B3A1A",
    });
  }
  if (data.matureEggs != null && data.matureEggs >= 0) {
    stages.push({
      id: "mature_oocytes",
      label: "Mature (MII)",
      count: data.matureEggs,
      color: "#A8563A",
    });
  }
  if (data.fertilized != null && data.fertilized >= 0) {
    stages.push({
      id: "fertilized_oocytes",
      label: "Fertilized",
      count: data.fertilized,
      color: "#B08030",
    });
  }
  if (data.day3Embryos != null && data.day3Embryos >= 0) {
    stages.push({
      id: "day3_embryos",
      label: "Day 3 Embryos",
      count: data.day3Embryos,
      color: "#5E6E3E",
    });
  }
  if (data.blastocysts != null && data.blastocysts >= 0) {
    stages.push({
      id: "blastocysts",
      label: "Blastocysts",
      count: data.blastocysts,
      color: "#4E7342",
    });
  }
  if (data.frozenEmbryos != null && data.frozenEmbryos >= 0) {
    stages.push({
      id: "frozen_embryos",
      label: "Embryos Frozen",
      count: data.frozenEmbryos,
      color: "#4E7342",
    });
  }
  if (data.euploid != null && data.euploid >= 0) {
    stages.push({
      id: "euploid_embryos",
      label: "Euploid (PGT-A)",
      count: data.euploid,
      color: "#6B5A4E",
    });
  }
  const projected = projectObservedFunnel(stages);
  if (data.noResult == null || data.noResult < 0) return projected;

  return [
    ...projected,
    {
      label: "No Result",
      count: data.noResult,
      color: "#9A877B",
    },
  ];
}

function projectObservedFunnel(stages: ObservedFunnelStage[]): FunnelStage[] {
  if (stages.length === 0) return [];

  const projection = projectFertilityPrediction({
    kind: "observed_funnel",
    patient: {},
    funnel: {
      stages: stages.map((stage) => ({
        id: stage.id,
        value: {
          value: stage.count,
          provenance: {
            sourceType: "clinic_record",
            sourceId: "cryovault-funnel-record",
          },
        },
      })),
    },
  });

  return projection.stages.flatMap((stage) => {
    const source = stages.find((candidate) => candidate.id === stage.id);
    return source
      ? [
          {
            label: source.label,
            count: Math.max(0, Math.round(stage.estimate.mean)),
            color: source.color,
            detail: source.detail,
          },
        ]
      : [];
  });
}
